import { useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { Icon } from '@/ui/Icon';
import { usePresence } from '@/lib/hooks';
import { ESC, TRAP, useEscape, useFocusTrap } from '@/lib/keys';
import { setComposerOpen } from '@/lib/overlays';
import { useStore } from '@/lib/store';
import { setToastsRaised } from '@/lib/toast';
import { Composer, type ComposerControl } from './Composer';
import { MOD } from './Editor';
import { composerStore, minimiseComposer, restoreComposer } from './state';

// The layer the composer lives in, and the small bar it shrinks to when minimised.
// The email stays mounted while minimised, so uploads carry on and nothing is lost.

const noop = () => undefined;

export function ComposerHost() {
  const { status, req, seq, snapshot } = useStore(composerStore, (s) => s);
  const open = status === 'open';
  const { mounted, shown } = usePresence(open, 300);
  const ref = useRef<HTMLElement>(null);
  const dock = useRef<HTMLDivElement>(null);
  const ctl = useRef<ComposerControl>({ close: noop, send: noop, focus: noop });
  const titleId = useId();
  const returnTo = useRef<HTMLElement | null>(null);
  const lastFocus = useRef<HTMLElement | null>(null);
  const [dockTitle, setDockTitle] = useState('');
  const wasOpen = useRef(false);

  useEffect(() => {
    setComposerOpen(open);
    setToastsRaised(status !== 'closed');
  }, [open, status]);
  useEffect(
    () => () => {
      setComposerOpen(false);
      setToastsRaised(false);
    },
    [],
  );

  // A new email forgets where the last one had focus.
  useEffect(() => {
    lastFocus.current = null;
  }, [seq]);

  useEffect(() => {
    const active = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
    const inside = (el: Element | null) => !!el && (!!ref.current?.contains(el) || !!dock.current?.contains(el));
    if (open && !wasOpen.current) {
      wasOpen.current = true;
      if (active && !inside(active)) returnTo.current = active;
      // Coming back from the bar: focus goes back where you were writing. A fresh email focuses its first empty field itself.
      const back = lastFocus.current;
      if (back) {
        const t = setTimeout(() => (back.isConnected ? back.focus({ preventScroll: true }) : ctl.current.focus()), 60);
        return () => clearTimeout(t);
      }
      return;
    }
    if (!open && wasOpen.current) {
      wasOpen.current = false;
      if (status === 'minimised') setDockTitle(document.getElementById(titleId)?.textContent || 'Draft');
      const to = returnTo.current;
      if (status === 'closed') returnTo.current = null;
      if (!active || inside(active)) {
        if (to && to.isConnected) to.focus({ preventScroll: true });
        else if (status === 'minimised') dock.current?.querySelector<HTMLElement>('button')?.focus();
      }
    }
  }, [open, status, titleId]);

  // The same keys that minimised the email bring it back.
  useEffect(() => {
    if (status !== 'minimised') return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.defaultPrevented || !(e.metaKey || e.ctrlKey) || e.altKey || e.key !== '.') return;
      e.preventDefault();
      restoreComposer();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [status]);

  useEscape(open, ESC.composer, () => {
    ctl.current.close();
  });
  useFocusTrap(ref, open, TRAP.composer);

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
    // Menus portaled out of the composer handle their own keys.
    if (!ref.current?.contains(e.target as Node)) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      ctl.current.send();
    } else if (e.key === '.') {
      e.preventDefault();
      minimiseComposer();
    } else if (e.key === 'k' || e.key === 'K') {
      // The palette stays shut while you write; in the message, the editor turns this into a link.
      e.preventDefault();
    }
  };

  const onFocus = (e: FocusEvent<HTMLElement>) => {
    if (e.target instanceof HTMLElement && ref.current?.contains(e.target)) lastFocus.current = e.target;
  };

  if (status === 'closed' && !mounted) return null;
  return (
    <>
      <div className={['layer', mounted ? 'open' : '', shown && open ? 'show' : ''].filter(Boolean).join(' ')} aria-hidden={open ? undefined : true}>
        <div className="scrim" onMouseDown={() => ctl.current.close()} />
        <section ref={ref} className="composer" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={onKeyDown} onFocus={onFocus}>
          <span className="grab" aria-hidden="true" />
          <Composer key={seq} req={req} snapshot={snapshot} ctl={ctl} titleId={titleId} open={open} />
        </section>
      </div>
      {status === 'minimised' ? (
        <div ref={dock} className="cmp-dock" role="region" aria-label="Minimised email">
          <button type="button" className="cmp-dock-t" onClick={restoreComposer} data-tip="Open the email" data-key={`${MOD} .`}>
            <Icon name="edit" />
            <span>{dockTitle}</span>
          </button>
          <button type="button" className="icon-btn sm" aria-label="Close and keep the draft" data-tip="Close" onClick={() => ctl.current.close()}>
            <Icon name="x" />
          </button>
        </div>
      ) : null}
    </>
  );
}
